import { useMemo, useCallback } from "react"

import * as turf from "@turf/turf"
import { useParams } from "react-router-dom"

import { CatalogItemsSource } from "@l2r-front/l2r-common"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { DEFAULT_SIGN_CODE, DEFAULT_SIGN_ICON, useMapDispatchContext } from "@l2r-front/l2r-map"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { useTheme } from "@l2r-front/l2r-ui"
import { findTypeByCode } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { getHorizontalSigningConditionsValues } from "../../constants/horizontalSigningConditions"
import { useHorizontalSigns } from "../../hooks/queries/horizontalSigning/useHorizontalSigns"
import { useHorizontalSignTypes } from "../../hooks/queries/horizontalSigning/useHorizontalSignTypes"
import { useHorizontalSignDescendantCodes } from "../../hooks/utils/useHorizontalSignDescendantCodes"

export function HorizontalSigningSource(props) {
    const {
        campaignId,
        filters,
        ...sourceProps
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const theme = useTheme()
    const { setError: setMapError } = useMapDispatchContext()
    const { signId } = useParams()

    const descendantCodes = useHorizontalSignDescendantCodes(filters?.type)
    const { selectedNetwork, selectedSubNetworkSlugs } = useNetworksStateContext()

    const { data: horizontalSignTypes } = useHorizontalSignTypes()

    const { data } = useHorizontalSigns({
        as_geojson: true,
        ...(campaignId && { campaign: campaignId }),
    }, {
        filterBySubnetworks: !campaignId,
        onError: () => setMapError(true),
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.horizontalSignsLayer.error"),
    })

    const getIcon = useCallback((typeCode) => {
        return typeCode === DEFAULT_SIGN_CODE ? DEFAULT_SIGN_ICON :
            findTypeByCode(typeCode, horizontalSignTypes)?.icon || DEFAULT_SIGN_ICON
    }, [horizontalSignTypes])

    // Need to copy id into properties because of that mapbox issue:
    // link:https://github.com/mapbox/mapbox-gl-js/issues/2716
    const sanitizedData = useMemo(() => {
        if (!data || !horizontalSignTypes) {
            return null
        }
        const dataFeatures = []
        turf.geomEach(data, (currentGeometry, featureIndex, featureProperties, featureBBox, featureId) => {
            const feature = data.features[featureIndex]
            const sign = featureProperties
            const isSelectedSign = featureId === signId
            const isSelectedType = descendantCodes.includes(sign.horizontal_sign_type)
            const isSelectedCondition = filters?.conditions ? (filters.conditions).includes(sign.condition)
                : true
            const isFilteredByTags = !filters?.tags || filters.tags.every(filterTag => sign.tags.includes(filterTag))
            const isFilteredByAntiTags = !filters?.antiTags || filters.antiTags.every(filterAntiTag => !sign.tags.includes(filterAntiTag))
            const disabled = !!campaignId && (!feature.properties.has_access ||
                (selectedNetwork.children?.length && !feature.properties.sub_network_slugs.some(slug => selectedSubNetworkSlugs.includes(slug)))
            )
            if (isSelectedSign || (isSelectedType && isSelectedCondition && isFilteredByTags && isFilteredByAntiTags)) {
                dataFeatures.push({
                    id: featureId,
                    bbox: featureBBox,
                    geometry: currentGeometry,
                    properties: {
                        ...featureProperties,
                        globalCondition: (sign.condition && !disabled) ? sign.condition : -1,
                        globalIcon: sign.horizontal_sign_type,
                        horizontal_signs: sign,
                        linearLocation: feature.linear_location,
                        uuid: featureId,
                        disabled,
                    },
                })
            }
        })
        return turf.featureCollection(dataFeatures)
    }, [
        campaignId,
        data,
        descendantCodes,
        horizontalSignTypes,
        selectedNetwork,
        selectedSubNetworkSlugs,
        signId,
        filters,
    ])

    if (!sanitizedData) {
        return null
    }

    return (
        <CatalogItemsSource
            {...sourceProps}
            getIcon={getIcon}
            iconOffset={0}
            legend={getHorizontalSigningConditionsValues(t, theme)}
            sanitizedData={sanitizedData}
            types={horizontalSignTypes}
        />
    )
}
